import type { RollupExperienceType } from '@/components/shared/ExperienceRollupCard'
import type {
  AnalyticsForecastQueue,
  AwaitingNextMeetingRow,
  CaseWorkKind,
  ForecastQueueRow,
  PostDeliveryWorkRow,
  UnscheduledReason,
} from '@/types/backofficeAnalytics.types'

/**
 * Client-side filter state for the Forecasts queue (sc-1212 round 2).
 *
 * The queue is a standing snapshot of ~175 rows fetched ONCE with no server
 * filters (see the route's own hook-call comment) — every one of these
 * fields is applied entirely in the browser. `band` is deliberately kept out
 * of this shape: it drills the table down but must NOT affect the rollup
 * cards' own counts, so the route applies it as a separate pass over rows
 * this filter has already accepted — see `forecasts/index.tsx`.
 */
export interface ForecastQueueRowFilters {
  /** Case-insensitive, matched against client name / subject / owner name /
   * stage label. Empty string means "no search". */
  search: string
  /** Empty array means "all experiences" — same convention as the
   * server-side filter it replaced. */
  experiences: Array<RollupExperienceType>
  /**
   * Empty array means "all owners".
   *
   * These are application USER ids, because that is what `UserPicker` emits —
   * matched against `row.owner.userId`, never `row.owner.id`, which is the
   * HubSpot owner id and would match nothing. A row whose owner maps to no
   * active staff member never matches a non-empty selection, which is the same
   * population the server-side filter excluded.
   */
  ownerIds: Array<string>
  /**
   * Whether Annual Review cases are included.
   *
   * **Defaults to `false`** (Tyler, 2026-09-29). It shipped defaulting to
   * `true` so that adding the control changed nothing for existing readers,
   * but the page's daily job is the Case Design book, and AR cases run their
   * own cycle on their own cadence — they were padding the queue rather than
   * informing it. A reader who wants them turns them on.
   */
  includeAnnualReviews: boolean
  /**
   * Empty array means "all kinds" — same permissive convention as
   * `experiences` above (sc-1212 round 5). **`ANNUAL_REVIEW` is
   * never offered as an option** — `includeAnnualReviews` already defaults
   * to hiding those rows and returns early above, so offering the kind here
   * too would give two controls that disagree about the same rows and a
   * selection of just `ANNUAL_REVIEW` that is guaranteed empty. See
   * `CASE_WORK_KIND_FILTER_OPTIONS` in `forecasts/index.tsx`.
   */
  caseWorkKinds: Array<CaseWorkKind>
  /**
   * There is deliberately no stage field here (sc-1212, stage pills). The
   * `Rework stage` dropdown that sat in this spot was the same cut as the
   * Revisions and Alt scenarios pills, so it went; the stage selection is
   * page-local state applied AFTER these predicates, because the pill counts
   * must be taken from rows that have not yet been narrowed by it. See
   * `forecast-stage-pills.ts`.
   */
  /**
   * Empty array means "all reasons" — same permissive convention as every
   * grouping above (sc-1212 round 7). Narrows to cases with no delivery
   * meeting booked, BY REASON, so the 2 delivered-and-forgotten rows can be
   * pulled out from under the 79 that correctly have nothing booked yet.
   *
   * A row WITH a deadline has `unscheduledReason === null` and so can never
   * match a non-empty selection — selecting any reason therefore implies "and
   * is unscheduled", which is what a reader means by picking one.
   */
  unscheduledReasons: Array<Exclude<UnscheduledReason, null>>
  /**
   * Empty array means "all planners" — same permissive convention as every
   * grouping above (sc-1212 round 15, Tyler: *"maybe use our employee picker
   * component as a filter and set up this tab to use that some how"*).
   *
   * **A DIFFERENT AXIS FROM `ownerIds`, not a rename of it.** `ownerIds` is
   * the Case Design SPECIALIST who holds the ticket; this is the client's
   * PLANNER, whose change requests and repeat meetings are the work being
   * priced. Two people, two jobs, two filters — conflating them has already
   * produced a wrong answer out loud, so they are kept apart in the state as
   * well as on screen.
   *
   * Application USER ids, because that is what `UserPicker` emits — matched
   * against `row.planner.userId`, never `row.planner.id`, which is the
   * `clients.planner_id` staff id. A planner who maps to no active staff
   * member therefore never matches a non-empty selection.
   *
   * **Applies only to the billable list**, the one population carrying a
   * planner. `matchesForecastQueueRowFilters` and
   * `matchesAwaitingNextMeetingFilters` deliberately ignore it, the same way
   * they ignore the queue-only filters — asserted in the inert-filter tests
   * rather than assumed.
   */
  plannerUserIds: Array<string>
}

/**
 * ## There is deliberately no `alertsOnly` here (sc-1212, red rows → Alerts view)
 *
 * It existed for one round, as a boolean beside `includeAnnualReviews` that
 * narrowed all nine tabs to the red rows. Tyler saw it working and replaced
 * it with a view: *"might be worth dynamically changing the tabs views
 * actually to a single Alerts view rather than looking through tabs to find
 * all the alerts, toggle just changes the main view to the table of alerts."*
 *
 * **Measuring it is what killed it.** Billable work came out 79% red, because
 * `REOPENED_AFTER_DELIVERY` is near-tautological on a list selected for cases
 * where delivery continued — so on that tab the colour said nothing, and
 * narrowing to it narrowed almost nothing. One Alerts view dissolves that: the
 * alert sits in a list that is supposed to be all alerts, instead of tinting a
 * tab where it is the entry condition.
 *
 * So the selection moved OUT of these predicates and into
 * `alerts-view-rows.ts`, which picks the alerted rows off the lists these
 * predicates have already produced. Red rows stay red in the normal tabs —
 * that part was right and was kept. **Do not add an alert condition back
 * here**: it would narrow the lists the Alerts view is built from, and the
 * view would then be the intersection of two rules that are supposed to be
 * one.
 */

export const DEFAULT_FORECAST_QUEUE_ROW_FILTERS: ForecastQueueRowFilters = {
  search: '',
  experiences: [],
  ownerIds: [],
  includeAnnualReviews: false,
  caseWorkKinds: [],
  unscheduledReasons: [],
  plannerUserIds: [],
}

/**
 * Whether `row` passes every filter EXCEPT the band selection — this is the
 * population the rollup cards count against, so clicking between bands
 * never makes the cards disagree with each other (sc-1212 round 2 "Counts").
 */
export function matchesForecastQueueRowFilters(
  row: ForecastQueueRow,
  filters: ForecastQueueRowFilters,
): boolean {
  if (!filters.includeAnnualReviews && row.isAnnualReview) return false

  if (
    filters.caseWorkKinds.length > 0 &&
    !filters.caseWorkKinds.includes(row.caseWorkKind)
  ) {
    return false
  }

  if (filters.unscheduledReasons.length > 0) {
    // `null` is a banded row, which no reason selection can match. Checked
    // explicitly rather than leaning on `includes(null)` being false, so the
    // intent survives a future widening of the union.
    if (
      row.unscheduledReason === null ||
      !filters.unscheduledReasons.includes(row.unscheduledReason)
    ) {
      return false
    }
  }

  if (filters.experiences.length > 0) {
    const wanted = new Set(filters.experiences)
    if (!row.experiences.some((experience) => wanted.has(experience))) {
      return false
    }
  }

  if (filters.ownerIds.length > 0) {
    if (!row.owner?.userId || !filters.ownerIds.includes(row.owner.userId)) {
      return false
    }
  }

  const term = filters.search.trim().toLowerCase()
  if (term) {
    const haystack = [
      row.client?.name,
      row.subject,
      row.owner?.name,
      row.stageLabel,
    ]
      .filter((value): value is string => !!value)
      .join(' \u0000 ')
      .toLowerCase()
    if (!haystack.includes(term)) return false
  }

  return true
}

/**
 * The same filters, applied to the "Delivered — awaiting next meeting" list
 * (sc-1212 round 7, Tyler 2026-10-05: *"it shows annual reviews, so having that
 * filter toggle to turn them on, and having them default to off is nice"*).
 *
 * That list shipped deliberately ignoring the page's filters, on the reasoning
 * that it draws from a different population. **That was wrong in practice**:
 * 36 of its 65 rows were Annual Reviews, which run their own cycle on their own
 * cadence — exactly the reason the queue hides them by default. A reader who
 * turns them off above and still sees 36 of them below does not conclude the
 * lists have different populations; they conclude the toggle is broken.
 *
 * **One of the queue's filters is deliberately NOT applied here**, because
 * it is meaningless for this population rather than merely unset:
 *
 * - `unscheduledReasons` — these rows have no band and no reason; by definition
 *   nothing is booked, which is the whole list.
 *
 * Kept as its own function rather than a branch inside
 * {@link matchesForecastQueueRowFilters}: the two row shapes have different
 * fields, and a shared predicate would have to widen its parameter until the
 * compiler stopped telling either caller what it actually needs.
 */
export function matchesAwaitingNextMeetingFilters(
  row: AwaitingNextMeetingRow,
  filters: ForecastQueueRowFilters,
): boolean {
  if (!filters.includeAnnualReviews && row.isAnnualReview) return false

  if (
    filters.caseWorkKinds.length > 0 &&
    !filters.caseWorkKinds.includes(row.caseWorkKind)
  ) {
    return false
  }

  if (filters.experiences.length > 0) {
    const wanted = new Set(filters.experiences)
    if (!row.experiences.some((experience) => wanted.has(experience))) {
      return false
    }
  }

  if (filters.ownerIds.length > 0) {
    if (!row.owner?.userId || !filters.ownerIds.includes(row.owner.userId)) {
      return false
    }
  }

  const term = filters.search.trim().toLowerCase()
  if (term) {
    // No stage label: every row is in Ready for Presentation, so including it
    // would make the search match every row on the word "ready".
    const haystack = [row.client?.name, row.subject, row.owner?.name]
      .filter((value): value is string => !!value)
      .join(' \u0000 ')
      .toLowerCase()
    if (!haystack.includes(term)) return false
  }

  return true
}

/**
 * The same filters, applied to the "Billable — work done after delivery" list
 * (sc-1212 round 14).
 *
 * **Only four of them can reach these rows, and the section's copy says so
 * rather than leaving a reader to wonder.** That matters because of what
 * happened the last time a delivered list quietly ignored the page's filters:
 * a toggle that changes nothing reads as broken, not as a different
 * population (see {@link matchesAwaitingNextMeetingFilters}).
 *
 * - `includeAnnualReviews` applies, and still defaults to hiding them. An AR
 *   that ran extra meetings has genuinely earned billable hours, so these rows
 *   are not noise here — but the toggle must mean one thing across every tab.
 * - `ownerIds` and `search` apply.
 * - `plannerUserIds` applies, and reaches THIS LIST ALONE (sc-1212 round 15) —
 *   it is the only population carrying a planner. It is a separate axis from
 *   `ownerIds`: owner is the Case Design specialist, planner is the client's
 *   planner, and a reader who means one never means the other.
 * - `experiences` and `caseWorkKinds` CANNOT: the row carries the ticket's raw
 *   `serviceType` and neither a resolved experience list nor a work kind, so
 *   there is nothing to compare. Filtering on a field that is not there would
 *   empty the list rather than narrow it.
 * - `unscheduledReasons` is meaningless here: this population spans every
 *   stage including Closed and has no band and no reason.
 *
 * `serviceType` is folded into the search haystack, which is the nearest thing
 * this list has to the Experience picker — a manager can still type "Annual
 * Review" or "IE" and get the rows.
 */
export function matchesPostDeliveryWorkFilters(
  row: PostDeliveryWorkRow,
  filters: ForecastQueueRowFilters,
): boolean {
  if (!filters.includeAnnualReviews && row.isAnnualReview) return false

  if (filters.ownerIds.length > 0) {
    if (!row.owner?.userId || !filters.ownerIds.includes(row.owner.userId)) {
      return false
    }
  }

  if (filters.plannerUserIds.length > 0) {
    // A row with no planner attributed can never match a selection — picking a
    // planner means "this planner's cases", and the unattributed case is by
    // definition not one of them. It stays visible with the filter off, where
    // the rollup reports it as its own bucket.
    if (
      !row.planner?.userId ||
      !filters.plannerUserIds.includes(row.planner.userId)
    ) {
      return false
    }
  }

  const term = filters.search.trim().toLowerCase()
  if (term) {
    // The stage IS included here, unlike the Ready-stage lists: these rows
    // genuinely span stages, so "closed" is a search a manager would mean.
    // The planner's name is in the haystack too, so typing a name finds their
    // cases whether the reader reaches for the search box or the picker.
    const haystack = [
      row.client?.name,
      row.subject,
      row.owner?.name,
      row.planner?.name,
      row.stage,
      row.serviceType,
    ]
      .filter((value): value is string => !!value)
      .join(' \u0000 ')
      .toLowerCase()
    if (!haystack.includes(term)) return false
  }

  return true
}

/**
 * Every working list, filtered — one object, one pass, one source of truth for
 * **what each tab shows** (sc-1212, Alerts view).
 *
 * The route used to hold seven near-identical `useMemo`s, each filtering one
 * section with whichever of the three predicates fitted it. That was fine
 * while the only reader of a list was the tab it belonged to. The Alerts view
 * has to draw from all eight at once, and the thing it must not do is
 * re-derive them: a view built off `data.urgent.rows` while the Urgent tab
 * renders a filtered copy would be a second, quietly different answer to "what
 * is on this page right now", and the owner picker would appear to work on the
 * tabs and not in the view. So the fan-out lives here, beside the predicates
 * it applies, and the route and `buildAlertsViewRows` read the same object.
 *
 * **Band is deliberately NOT applied.** These are the pre-band populations the
 * rollup tiles count against (see {@link matchesForecastQueueRowFilters}); the
 * route narrows the three band-filtered lists itself, after the tiles have
 * counted them.
 *
 * **`cleaning` is absent and cannot be added.** Needs Cleaned is server-paged
 * off its own endpoint, and its rows are findings about records rather than
 * cases — they carry no `sequence` and nothing these predicates read.
 *
 * Keyed by `LIST_TABS` values, so a tab and its filtered rows cannot end up
 * named differently in two places.
 */
export interface FilteredForecastLists {
  /** Pre-band — see the doc comment. */
  urgent: Array<ForecastQueueRow>
  /** Pre-band. */
  scheduled: Array<ForecastQueueRow>
  /** Pre-band. */
  undated: Array<ForecastQueueRow>
  /**
   * Parked rows take every row filter and NOT the band: a band is a statement
   * about making a deadline, and a parked case has been judged unable to make
   * it, so narrowing these by band would hide the rows the list exists to
   * show.
   */
  parked: Array<ForecastQueueRow>
  awaiting: Array<AwaitingNextMeetingRow>
  workDue: Array<AwaitingNextMeetingRow>
  readyToClose: Array<AwaitingNextMeetingRow>
  postDelivery: Array<PostDeliveryWorkRow>
}

/** Every list empty — the shape the page renders while the queue loads. */
const EMPTY_FORECAST_LISTS: FilteredForecastLists = {
  urgent: [],
  scheduled: [],
  undated: [],
  parked: [],
  awaiting: [],
  workDue: [],
  readyToClose: [],
  postDelivery: [],
}

/**
 * Pure, and it takes `data | undefined` on purpose: the route calls it before
 * the fetch resolves, and every section is read as possibly absent even where
 * the type says otherwise — `apps/web` and `apps/api` deploy separately, so a
 * payload can arrive without a section this bundle expects. An absent section
 * reads as an empty list, never as a crash.
 */
export function filterForecastLists(
  data: AnalyticsForecastQueue | undefined,
  filters: ForecastQueueRowFilters,
): FilteredForecastLists {
  if (!data) return EMPTY_FORECAST_LISTS
  /**
   * Each section is taken as `| undefined` although the payload type declares
   * it required, and the `?.` lives HERE rather than at the eight call sites
   * on purpose: in a parameter position it is the honest signature of a
   * separately-deployed payload, while at the call sites the compiler reads it
   * as dead defensiveness and `no-unnecessary-condition` says so eight times.
   */
  const queue = (section: { rows: Array<ForecastQueueRow> } | undefined) =>
    (section?.rows ?? []).filter((row) =>
      matchesForecastQueueRowFilters(row, filters),
    )
  const ready = (
    section: { rows: Array<AwaitingNextMeetingRow> } | undefined,
  ) =>
    (section?.rows ?? []).filter((row) =>
      matchesAwaitingNextMeetingFilters(row, filters),
    )
  const billable = (
    section: { rows: Array<PostDeliveryWorkRow> } | undefined,
  ) =>
    (section?.rows ?? []).filter((row) =>
      matchesPostDeliveryWorkFilters(row, filters),
    )
  return {
    urgent: queue(data.urgent),
    scheduled: queue(data.scheduled),
    // `unscheduled` on the wire, `undated` on screen — the tab was renamed and
    // the payload was not.
    undated: queue(data.unscheduled),
    parked: queue(data.parked),
    awaiting: ready(data.awaitingNextMeeting),
    workDue: ready(data.upcomingDeliveryWork),
    // The same predicate as its two sibling Ready-stage lists: same row shape,
    // same Ready population.
    readyToClose: ready(data.readyToClose),
    postDelivery: billable(data.postDeliveryWork),
  }
}
